import { affiliationService } from "app/services/affiliation.service";
import { CommissionStatus } from "types/affiliation.types";
import { useMemo, useState } from "react";
import { EarningsPieChartKeys } from "features/affiliation/common/types";
import { toFixed } from "@shared/utils/numbers";
import {
  EARNINGS_PIE_CHART_KEY_TO_STATUS,
  USD_PRECISION,
} from "features/affiliation/common/constants";
import {
  formatTimeSeriesChartData,
  preprocessPieChartData,
} from "@shared/utils/charts";
import { useTheme } from "styled-components";
import moment from "moment";
import { DatePickerProps } from "antd/es/date-picker";

export const useEarnings = () => {
  const commissionHistoryQuery =
    affiliationService.useGetCommissionHistoryQuery();
  const commissions = commissionHistoryQuery.data;

  const sumCommissions = (statusFilter?: CommissionStatus) => {
    let commissionsToSum = commissions;
    if (statusFilter)
      commissionsToSum = commissions?.filter(
        (commission) => commission.status === statusFilter
      );
    return (
      commissionsToSum?.reduce(
        (sum, commission) => sum + commission.commission,
        0
      ) || 0
    );
  };

  return {
    commissionHistoryQuery,
    commissions,
    sumCommissions,
  };
};

export const useEarningsPieChart = () => {
  const appTheme = useTheme();
  const { sumCommissions, commissions, commissionHistoryQuery } = useEarnings();

  const earningsPieChartRawData = useMemo(
    () =>
      Object.values(EarningsPieChartKeys).map((key) => {
        return {
          name: key,
          value: toFixed(
            sumCommissions(EARNINGS_PIE_CHART_KEY_TO_STATUS[key]),
            USD_PRECISION
          ),
        };
      }),
    [commissions]
  );

  const earningsPreprocessPieChart = useMemo(
    () =>
      preprocessPieChartData(
        earningsPieChartRawData,
        appTheme.colors.pieChartColors,
        true
      ),
    [earningsPieChartRawData, appTheme]
  );

  return {
    commissions,
    sumCommissions,
    commissionHistoryQuery,
    earningsPreprocessPieChart,
  };
};

export const useEarningsBarChart = () => {
  const { commissions } = useEarnings();

  const [chartSelectedMonth, setChartSelectedMonth] = useState(
    moment().month() + 1
  );
  const [chartSelectedYear, setChartSelectedYear] = useState(moment().year());

  const args = { year: chartSelectedYear, month: chartSelectedMonth };
  const earningsChartQuery = affiliationService.useGetEarningsChartQuery(args);
  const earningsByMonthChartData = earningsChartQuery?.data;

  const formattedBarChartData = useMemo(
    () =>
      formatTimeSeriesChartData(
        earningsByMonthChartData || [],
        undefined,
        undefined,
        undefined
      ),
    [earningsByMonthChartData]
  );

  const onDataPickerChange: DatePickerProps["onChange"] = (date) => {
    if (date && !Array.isArray(date)) {
      setChartSelectedMonth(date.month() + 1);
      setChartSelectedYear(date.year());
    }
  };

  return {
    earningsChartQuery,
    formattedBarChartData,
    onDataPickerChange,
    commissions,
  };
};
